import React from "react";
import "./tag-filter-bar.scss";

/** Single tag item: string or object with optional id and label */
export type TagFilterBarTag = string | { id?: string; label: string; title?: string };

export interface TagFilterBarProps {
  /** Tags to display. Can be strings or objects with id/label. */
  tags: TagFilterBarTag[];
  /** Called when a tag's remove (X) icon is clicked. Receives the tag label and optional index. */
  onTagRemove?: (label: string, index: number) => void;
  /** Called when the dropdown/add-more button is clicked. */
  onDropdownClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Whether to show the dropdown chevron button on the right. Default true. */
  showDropdown?: boolean;
  /** Whether each tag shows a remove (X) icon. Default true. */
  closable?: boolean;
  /** Optional dropdown button aria-label. */
  dropdownAriaLabel?: string;
  /** Optional class for the root container. */
  className?: string;
  /** Optional inline style for the root container. */
  style?: React.CSSProperties;
  /** Tag background color (default from design: #f5f5f5). */
  tagBackgroundColor?: string;
  /** Tag text color (default from design: #0f0f0f). */
  tagTextColor?: string;
  /** Tag border color (default from design: #0f0f0f). */
  tagBorderColor?: string;
  /** Custom close icon (must be sized appropriately, ~14px). */
  closeIcon?: React.ReactNode;
  /**
   * When true, tags stay on a single line and the tags area scrolls horizontally.
   * When false (default), tags wrap to the next line.
   */
  singleLineScrollable?: boolean;
}

function normalizeTag(tag: TagFilterBarTag, index: number): { key: string; label: string; title?: string } {
  if (typeof tag === "string") {
    return { key: `tag-${index}-${tag}`, label: tag };
  }
  return {
    key: tag.id ?? `tag-${index}-${tag.label}`,
    label: tag.label,
    title: tag.title,
  };
}

/** Small X icon for tag remove, 14x14 to match Figma. */
function TagCloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** 16x16 chevron down to match Figma. */
function ChevronDownIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M4 6L8 10L12 6"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const TagFilterBar: React.FC<TagFilterBarProps> = ({
  tags,
  onTagRemove,
  onDropdownClick,
  showDropdown = true,
  closable = true,
  dropdownAriaLabel = "Add or select more filters",
  className = "",
  style,
  tagBackgroundColor,
  tagTextColor,
  tagBorderColor,
  closeIcon,
  singleLineScrollable = false,
}) => {
  return (
    <div
      className={`tag-filter-bar ${singleLineScrollable ? "tag-filter-bar--single-line" : ""} ${className}`}
      style={style}
      role="group"
      aria-label="Tag filters"
    >
      <div className="tag-filter-bar__tags">
        {tags.map((tag, index) => {
          const { key, label, title } = normalizeTag(tag, index);
          const canRemove = closable && Boolean(onTagRemove);
          return (
            <div
              key={key}
              className="tag-filter-bar__tag"
              title={title || label}
              style={{
                ...(tagBackgroundColor && { backgroundColor: tagBackgroundColor }),
                ...(tagTextColor && { color: tagTextColor }),
                ...(tagBorderColor && { borderColor: tagBorderColor }),
              }}
            >
              <span className="tag-filter-bar__tag-label">{label}</span>
              {canRemove && (
                <button
                  type="button"
                  className="tag-filter-bar__tag-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    onTagRemove?.(label, index);
                  }}
                  aria-label={`Remove ${label}`}
                  style={tagTextColor ? { color: tagTextColor } : undefined}
                >
                  {closeIcon ?? <TagCloseIcon />}
                </button>
              )}
            </div>
          );
        })}
      </div>
      {showDropdown && (
        <button
          type="button"
          className="tag-filter-bar__dropdown"
          onClick={onDropdownClick}
          aria-label={dropdownAriaLabel}
          aria-haspopup="listbox"
        >
          <ChevronDownIcon />
        </button>
      )}
    </div>
  );
};
